'use client'

import { ColumnDef } from '@tanstack/react-table'
import { Ellipsis, Mail } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DateDisplay } from '@/components/date-display'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ResourceBadge } from '@/components/ui/resource-badge'
import { TaxExemptionItem } from '@/lib/api/taxExemptions'

interface GetTaxExemptionsTableColumnsProps {
	onDeleteTaxExemption: (id: string) => void
}

export function useTaxExemptionsTableColumns({
	onDeleteTaxExemption,
}: GetTaxExemptionsTableColumnsProps): ColumnDef<TaxExemptionItem>[] {
	const { t } = useTranslation('taxes')

	return React.useMemo(
		() => [
			{
				id: 'select',
				header: ({ table }) => (
					<Checkbox
						checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
						onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
						aria-label={t('table.selectAll')}
						className="translate-y-0.5"
					/>
				),
				cell: ({ row }) => (
					<DataTableCheckbox row={row} />
				),
				enableSorting: false,
				enableHiding: false,
				size: 40,
			},
			{
				id: 'id',
				accessorKey: 'id',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('exemptions.table.columns.id')} />,
				cell: ({ row }) => {
					const exemption = row.original
					return (
						<ResourceBadge
							name="exemption"
							value={`${exemption.id.substring(0, 8)}...`}
							variant="secondary"
						/>
					)
				},
				enableSorting: false,
			},
			{
				id: 'email',
				accessorKey: 'email',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('exemptions.table.columns.email')} />,
				cell: ({ row }) => {
					const exemption = row.original
					return (
						<div className="min-w-0">
							<div className="truncate font-medium">{exemption.email}</div>
						</div>
					)
				},
				meta: {
					label: t('exemptions.table.columns.email'),
					placeholder: t('exemptions.table.searchPlaceholder'),
					variant: 'text',
					icon: () => <Mail />,
				},
				enableColumnFilter: false,
				enableSorting: false,
			},
			{
				id: 'createdAt',
				accessorKey: 'createdAt',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('exemptions.table.columns.createdAt')} />,
				cell: ({ row }) => (
					<DateDisplay
						value={row.original.createdAt}
						mode="date"
						className="text-muted-foreground text-sm"
						fallback="-"
					/>
				),
				enableSorting: false,
			},
			{
				id: 'actions',
				cell: ({ row }) => {
					const exemption = row.original
					return (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									aria-label={t('table.openMenu')}
									variant="ghost"
									className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
								>
									<Ellipsis className="h-4 w-4" aria-hidden="true" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-[160px]">
								<DropdownMenuItem
									onClick={() => onDeleteTaxExemption(exemption.id)}
									className="text-destructive focus:text-destructive"
								>
									{t('table.deleteAction')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)
				},
				enableSorting: false,
				enableHiding: false,
				size: 40,
			},
		],
		[t, onDeleteTaxExemption]
	)
}
